// models
import Brand from '../models/Brand';
import Contract from '../models/Contract';
import ContractType from '../models/ContractType';
import HeroNews from '../models/HeroNews';
import News from '../models/News';
import NewsCategory from '../models/NewsCategory';
import ParticipantApproval from '../models/ParticipantApproval';
import Product from '../models/Product';
import ProductCategory from '../models/ProductCategory';
import Rank from '../models/Rank';
import Transaction from '../models/Transaction';
import { User, Profile } from '../models';
import Wallet from '../models/Wallet';
import WishlistEntry from '../models/WishlistEntry';
import Company from '../models/Company';
import Achievement from '../models/Achievement';
import Cart from '../models/Cart';
import OwnerStatistic from '../models/OwnerStatistic';
import Challenge from '../models/Challenge';
import Order from '../models/Order';
import CompletedOrder from '../models/CompletedOrder';
// translation management
import { i18n } from '../plugins/vuetify';
// utils
import env from './env';
import {
    API_ENDPOINT_FETCH_APPROVALS,
    LOCAL_STORAGE_JWT,
    LOCAL_STORAGE_TOKEN_EXPIRES,
    LOCAL_STORAGE_USER,
    ROUTE_LOGIN
} from './constants';
// router
import router from '../router/index';
// store
import Store from '../store/index';
import { api, authHeaders, errorHandler } from './api';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

export const deleteUserRelatedData = () => {
    Brand.deleteAll();
    Contract.deleteAll();
    ContractType.deleteAll();
    HeroNews.deleteAll();
    News.deleteAll();
    NewsCategory.deleteAll();
    ParticipantApproval.deleteAll();
    Product.deleteAll();
    ProductCategory.deleteAll();
    Profile.deleteAll();
    Rank.deleteAll();
    Transaction.deleteAll();
    User.deleteAll();
    Wallet.deleteAll();
    WishlistEntry.deleteAll();
    Company.deleteAll();
    Achievement.deleteAll();
    Cart.deleteAll();
    OwnerStatistic.deleteAll();
    Challenge.deleteAll();
    Order.deleteAll();
    CompletedOrder.deleteAll();
};

export const setPageTitle = (title, dynamicTitle) => {
    if (dynamicTitle) {
        const titleToSet = title
            ? `${i18n.t(`$vuetify.title.${title}`)} -`
            : '';
        return `${dynamicTitle} - ${titleToSet} ${env('name')}`;
    } else {
        return `${i18n.t(`$vuetify.title.${title}`)} - ${env('name')}`;
    }
};

export const showUnavailableScreen = () => {
    Store.dispatch('setBoolean', {
        stateToSet: 'showUnavailableScreen',
        value: true
    });
};

export const logOutUser = (keepLoginFormClosed, redirectTo) => {
    Store.dispatch('setBoolean', { stateToSet: 'init', value: false });
    if (!keepLoginFormClosed) {
        Store.dispatch('openLoginForm');
    }
    Store.dispatch('closeSidebar');

    // reset tour
    Store.dispatch('set', { item: 'tours', value: false });
    Store.dispatch('set', { item: 'tourIntro', value: false });
    Store.dispatch('set', { item: 'tourDashboard', value: false });
    Store.dispatch('set', { item: 'tourShop', value: false });

    deleteUserRelatedData();
    if (!redirectTo) {
        redirect(ROUTE_LOGIN);
    } else {
        Store.dispatch('closeLoginForm');
        redirect(redirectTo);
    }
    localStorage.removeItem(LOCAL_STORAGE_JWT);
    localStorage.removeItem(LOCAL_STORAGE_USER);
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_EXPIRES);
};

export const redirect = (to, params) => {
    // prevent redirecting to same page
    if (
        router &&
        router.history &&
        router.history.current &&
        router.history.current.name === to
    ) {
        return false;
    }

    router.push({ name: to, params });
};

export const transformArrayToAutoSelectValues = entity => {
    const retValue = [];
    Object.keys(entity).map(e => {
        retValue.push({
            text: `${e} (${entity[e]})`,
            value: entity[e]
        });
    });
    return retValue;
};

export const transformArrayToSelectValues = (entity, translationString) => {
    const retValue = [];
    entity.map(e => {
        retValue.push({
            text: i18n.t(`${translationString}${e}`),
            value: e
        });
    });
    return retValue;
};

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
 * @source https://stackoverflow.com/a/21742107
 * @returns {String}
 */
export const getMobileOperatingSystem = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
        return 'android';
    }
    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'ios';
    }
    return 'unknown';
};

export const evaluatePlatform = () => {
    if (window && window.matchMedia('(display-mode: standalone)').matches) {
        return 'pwa';
    } else if (window.hasOwnProperty('cordova')) {
        return 'app';
    }
    return 'web';
};

export const isFirebaseSettingAvailable = () => {
    return !!(
        env('firebase-api-key') &&
        env('firebase-auth-domain') &&
        env('firebase-database-url') &&
        env('firebase-project-id') &&
        env('firebase-storage-bucket') &&
        env('firebase-messaging-sender-id') &&
        env('firebase-app-id') &&
        env('firebase-measurement-id') &&
        env('firebase-vapid-key')
    );
};

export const hexToRgbA = (hex, alpha) => {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');
        return (
            'rgba(' +
            [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') +
            ', ' +
            alpha +
            ')'
        );
    }
    throw new Error('Bad Hex');
};

export const shallowEqual = (object1, object2) => {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (let key of keys1) {
        if (object1[key] !== object2[key]) {
            return false;
        }
    }

    return true;
};

export const initDeviceToken = async () => {
    if (Store.state.deviceToken === null) return;

    const deviceToSubmit = {
        token: Store.state.deviceToken,
        device_uuid: null
    };

    let deviceAlreadyExists = false;
    const currentUser = User.getCurrent();

    if (currentUser) {
        currentUser.device_tokens.forEach(device => {
            const _deviceAlreadyExists = shallowEqual(device, deviceToSubmit);
            if (_deviceAlreadyExists) {
                deviceAlreadyExists = _deviceAlreadyExists;
            }
        });

        if (!deviceAlreadyExists) {
            User.submitDevice(deviceToSubmit);
        }
    }

    return true;
};

const images = [
    {
        name: 'student',
        default: require(`../assets/images/medals/student.png`),
        completed: require(`../assets/images/medals/student-completed.png`)
    },
    {
        name: 'newsreader',
        default: require(`../assets/images/medals/newsreader.png`),
        completed: require(`../assets/images/medals/newsreader-completed.png`)
    },
    {
        name: 'seller',
        default: require(`../assets/images/medals/seller.png`),
        completed: require(`../assets/images/medals/seller-completed.png`)
    },
    {
        name: 'ihk',
        default: require(`../assets/images/medals/ihk.png`),
        completed: require(`../assets/images/medals/ihk-completed.png`)
    },
    {
        name: 'faithful',
        default: require(`../assets/images/medals/faithful.png`),
        completed: require(`../assets/images/medals/faithful-completed.png`)
    }
];

export const medalImage = (name, completed) => {
    if (completed) {
        return searchInArrayOfObjects(name, images)['completed'];
    } else {
        return searchInArrayOfObjects(name, images)['default'];
    }
};

export const searchInArrayOfObjects = (nameKey, myArray) => {
    for (let i = 0; i < myArray.length; i++) {
        if (myArray[i].name === nameKey) {
            return myArray[i];
        }
    }
};

export const checkDownloadPermission = async (url, id) => {
    if (url.includes('/api/')) {
        Store.dispatch('wait/start', `download-${id}`, { root: true });
        const response = await api.get(url, authHeaders()).catch(errorHandler);
        download(response.data.download);
        Store.dispatch('wait/end', `download-${id}`, { root: true });
    } else {
        download(url);
    }
};

export const download = async url => {
    if (Capacitor.isNativePlatform()) {
        await Browser.open({ url });
    } else {
        window.open(url, '_system');
        return false;
    }
};

export const checkNotifications = async () => {
    let permission = null;

    const checkPermissionResult = await FirebaseMessaging.checkPermissions();
    permission = checkPermissionResult.receive;

    if (permission === 'prompt' || permission === 'prompt-with-rationale') {
        const requestPermissionResult = await FirebaseMessaging.requestPermissions();
        permission = requestPermissionResult.receive;
    }

    if (permission === 'granted') {
        let token = null;
        if (!Capacitor.isNativePlatform()) {
            const getTokenResult = await FirebaseMessaging.getToken({
                vapidKey: env('firebase-vapid-key')
            });
            token = getTokenResult.token;
        } else {
            const getTokenResult = await FirebaseMessaging.getToken();
            token = getTokenResult.token;
        }

        await Store.dispatch('set', {
            item: 'deviceToken',
            value: token
        });
        await initDeviceToken();
    }
};

export const handleDeepLink = stringUrl => {
    const url = new URL(stringUrl.replace('/#', ''));
    const pathParam = url.searchParams.get('path');

    if (pathParam) {
        router.push({ path: `/${pathParam}` });
    } else {
        router.push({ path: url.pathname });
    }
};
